import { useState, useEffect, useRef } from 'react';
import { Sparkles, X, Share, MoreVertical, Menu } from 'lucide-react';

const DISMISSED_KEY = 'danny_pwa_dismissed';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const SHOW_DELAY_MS = 3000;

type BrowserInfo = {
  name: string;
  instruction: string;
  icon: 'share' | 'dots' | 'menu';
};

function detectBrowser(): BrowserInfo {
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);

  if (isIos) {
    // On iOS, all browsers use WebKit — but the UI differs
    if (/CriOS/.test(ua)) {
      return { name: 'Chrome', instruction: 'Tap ••• then "Add to Home Screen"', icon: 'dots' };
    }
    if (/FxiOS/.test(ua)) {
      return { name: 'Firefox', instruction: 'Tap the menu, then "Share" → "Add to Home Screen"', icon: 'menu' };
    }
    if (/EdgiOS/.test(ua)) {
      return { name: 'Edge', instruction: 'Tap ••• then "Add to phone"', icon: 'dots' };
    }
    if (/Brave/.test(ua)) {
      return { name: 'Brave', instruction: 'Open in Safari to add to Home Screen', icon: 'share' };
    }
    // Default to Safari on iOS
    return { name: 'Safari', instruction: 'Tap the share button, then "Add to Home Screen"', icon: 'share' };
  }

  if (isAndroid) {
    if (/SamsungBrowser/.test(ua)) {
      return { name: 'Samsung Internet', instruction: 'Tap the menu, then "Add page to" → "Home screen"', icon: 'menu' };
    }
    if (/Brave/.test(ua)) {
      return { name: 'Brave', instruction: 'Tap ••• then "Install app"', icon: 'dots' };
    }
    if (/EdgA/.test(ua)) {
      return { name: 'Edge', instruction: 'Tap ••• then "Add to phone"', icon: 'dots' };
    }
    if (/Firefox/.test(ua)) {
      return { name: 'Firefox', instruction: 'Tap ••• then "Install"', icon: 'dots' };
    }
    // Default Chrome on Android (also handles Opera, etc.)
    return { name: 'Chrome', instruction: 'Tap ••• then "Install app"', icon: 'dots' };
  }

  // Desktop fallback (shouldn't show, but just in case)
  return { name: 'browser', instruction: 'Install this app from your browser menu', icon: 'dots' };
}

function BrowserIcon({ type }: { type: BrowserInfo['icon'] }) {
  const cls = 'w-4 h-4 inline text-primary-400';
  switch (type) {
    case 'share': return <Share className={cls} />;
    case 'dots': return <MoreVertical className={cls} />;
    case 'menu': return <Menu className={cls} />;
  }
}

export function PwaInstallBanner() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<Event | null>(null);
  const deferredPromptRef = useRef<Event | null>(null);

  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as unknown as Record<string, unknown>).standalone === true);

  const browser = detectBrowser();

  useEffect(() => {
    if (isStandalone) return;

    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < SEVEN_DAYS_MS) return;
    }

    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    if (!isMobile) return;

    // Capture Android/Chrome install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const timer = setTimeout(() => setShow(true), SHOW_DELAY_MS);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
  }, [isStandalone]);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  };

  const handleInstall = async () => {
    const prompt = deferredPromptRef.current as (Event & { prompt: () => void; userChoice: Promise<{ outcome: string }> }) | null;
    if (prompt) {
      prompt.prompt();
      const result = await prompt.userChoice;
      if (result.outcome === 'accepted') dismiss();
    }
  };

  if (!show) return null;

  return (
    <div className="md:hidden fixed bottom-16 inset-x-0 z-40 px-4 pb-2 safe-area-bottom">
      <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-xl p-4 shadow-lg flex items-start gap-3">
        {/* App icon */}
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
            Get the full experience
          </p>
          {deferredPrompt ? (
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
              Install Danny for quick access
            </p>
          ) : (
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1 flex items-center gap-1 flex-wrap">
              Tap <BrowserIcon type={browser.icon} /> then <span className="font-semibold text-text-primary-light dark:text-text-primary-dark">"Add to Home Screen"</span>
            </p>
          )}
        </div>

        {/* Action button */}
        {deferredPrompt ? (
          <button
            onClick={handleInstall}
            className="px-3 py-1.5 bg-primary-500 text-surface-base-dark text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors flex-shrink-0"
          >
            Install
          </button>
        ) : (
          <button
            onClick={dismiss}
            className="px-3 py-1.5 bg-primary-500 text-surface-base-dark text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors flex-shrink-0"
          >
            Got it
          </button>
        )}

        {/* Dismiss */}
        <button
          onClick={dismiss}
          className="p-1 text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
